import React, { useCallback } from 'react';
import { OnLinkClickType, S4AMastheadProvider } from '@spotify-internal/s4x-masthead';
import { useRouter } from 'next/router';

import { SUPPORT_SITE_CLIENT_ID } from 'utils/config';
import { sendCounterMetric, Whats } from 'utils/metrics';
import { pathWithoutMarket } from 'utils/market/market';
import { MastheadVariants } from 'utils/vertical';

import { useServerSideConstants } from './ServerSideConstantsProvider';

// S4A's contentful configuration uses a different code for locales similar to what we have:
const susiLocaleToContentfulLocale: Record<string, string> = {
  'es-LA': 'es-419',
  'zh-TW': 'zh-Hant-TW',
};

function normalizeLocale(locale: string): string {
  return susiLocaleToContentfulLocale[locale] ?? locale;
}

interface Props {
  children: React.ReactNode;
}

const S4AMastheadProviderWrapper = ({ children }: Props) => {
  const {
    verticalConfig: {
      features: { configureMasthead },
    },
    market: { dir, locale, displayNameWithLanguage },
  } = useServerSideConstants();
  const marketSelectReturnToPath = pathWithoutMarket(useRouter().asPath);

  const onS4ALinkClick = useCallback(({ trackingAttributes }: OnLinkClickType) => {
    // trackingAttributes is usually a tuple of ["Page Context", "Click Context"]
    const actionContext = trackingAttributes[1].toLowerCase().replace(/\s/g, '_');
    sendCounterMetric(Whats.S4X_MASTHEAD, {
      clicked: actionContext,
    });
  }, []);

  if (configureMasthead?.header !== MastheadVariants.S4A) {
    return <>{children}</>;
  }

  return (
    <S4AMastheadProvider
      clientId={SUPPORT_SITE_CLIENT_ID}
      onLinkClick={onS4ALinkClick}
      language={{
        dir,
        locale: normalizeLocale(locale),
        useCountryRegionPicker: true,
        market: displayNameWithLanguage,
        marketSelectReturnToPath,
      }}
    >
      {children}
    </S4AMastheadProvider>
  );
};

export default S4AMastheadProviderWrapper;
